import { useId, type ReactElement } from 'react';
import { Micro } from '@/components/atoms/micro';
import type { Arrangement } from '@/lib/types';

export type ArrangementSubPickerProps = {
  arrangements: readonly Arrangement[];
  selectedArrangementId: string;
  userArrangementByArrangementId: ReadonlyMap<string, string>;
  onSelect: (arrangementId: string) => void;
};

function arrangementSubtitle(arrangement: Arrangement): string {
  const parts: string[] = [];
  if (arrangement.keyName !== null) parts.push(arrangement.keyName);
  if (arrangement.formName !== null) parts.push(arrangement.formName);
  if (arrangement.arrangerName !== null) parts.push(arrangement.arrangerName);
  return parts.join(' · ');
}

export function ArrangementSubPicker({
  arrangements,
  selectedArrangementId,
  userArrangementByArrangementId,
  onSelect,
}: ArrangementSubPickerProps): ReactElement | null {
  const groupName = useId();
  if (arrangements.length <= 1) return null;
  return (
    <fieldset role="radiogroup" aria-label="Setting" className="flex flex-col gap-3">
      <legend className="sr-only">Setting</legend>
      <Micro>Setting</Micro>
      <div className="flex flex-col gap-2">
        {arrangements.map((arrangement) => {
          const isInRep = userArrangementByArrangementId.has(arrangement.arrangementId);
          const isSelected = arrangement.arrangementId === selectedArrangementId;
          const subtitle = arrangementSubtitle(arrangement);
          return (
            <label
              key={arrangement.arrangementId}
              className={[
                'flex items-start gap-3 rounded-[var(--r-sm)] border p-4 text-sm transition-colors',
                isInRep
                  ? 'cursor-not-allowed border-[var(--rule)] bg-[var(--bg-soft)] opacity-60'
                  : 'cursor-pointer border-[var(--rule)] bg-[var(--bg-surface)] hover:border-[var(--brand)]',
                isSelected && !isInRep ? 'border-[var(--brand)] ring-1 ring-[color:var(--brand)]/30' : '',
              ].join(' ')}
            >
              <input
                type="radio"
                name={groupName}
                value={arrangement.arrangementId}
                checked={isSelected && !isInRep}
                disabled={isInRep}
                onChange={() => {
                  if (!isInRep) onSelect(arrangement.arrangementId);
                }}
                className="mt-0.5 h-4 w-4 flex-none accent-[var(--brand)]"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[var(--ink)]">{arrangement.prettyName}</p>
                {subtitle !== '' && (
                  <p className="mt-0.5 text-xs text-[var(--ink-3)]">{subtitle}</p>
                )}
              </div>
              {isInRep && (
                <span className="rounded-full border border-[var(--rule)] bg-[var(--bg-page)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--ink-3)]">
                  Already in repertoire
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
